import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, of } from 'rxjs';

import { JwtInfo, JwtInfoSchema } from '@hsc/common/schemas/eua.schema.js';
import { validateResponseToSchema } from '@hsc/common/utilities/zod-validation';
import { DeveloperTools } from '@components/developer-tools/developer-tools';
import { Footer } from '@components/footer';
import { Header } from '@components/header';
import { FullStoryService } from '@services/fullstory.service';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { NewRelicService } from '@services/new-relic.service';
import { SplunkLoggerService } from '@services/splunk-logger.service';

@Component({
  selector: 'hsc-root',
  imports: [RouterOutlet, Header, DeveloperTools, Footer],
  template: `
    <hsc-header />
    <hsc-developer-tools />
    <router-outlet />
    <hsc-footer />
  `,
})
export class App implements OnInit {
  private readonly fullStoryService = inject(FullStoryService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly newRelicService = inject(NewRelicService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly splunkLoggerService = inject(SplunkLoggerService);

  ngOnInit(): void {
    this.oidcSecurityService
      .checkAuth()
      .pipe(
        catchError((error) => {
          this.splunkLoggerService.error(error, { message: 'Error during authentication check' });
          this.newRelicService.noticeError(error);
          void this.router.navigate(['/error']);

          return of({ isAuthenticated: false } as LoginResponse);
        }),
      )
      .subscribe((loginResponse: LoginResponse) => {
        const { isAuthenticated } = loginResponse;
        if (!isAuthenticated) {
          this.splunkLoggerService.warn('User is not authenticated');
          return;
        }

        const jwtInfo: JwtInfo = validateResponseToSchema(loginResponse.userData, JwtInfoSchema);
        this.splunkLoggerService.info('User authenticated');

        this.newRelicService.identifyUser(jwtInfo);
        this.fullStoryService.setUserVars(jwtInfo);
        this.launchDarklyService.identifyUser(jwtInfo);
      });
  }
}
